import React, { useState, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useGetCalls } from '../../../../../features/calls/hooks/useGetCalls';
import FilterableTable from '../../../../common/table/FilterableTable';

/**
 * AllCalls - displays paginated calls with server-side pagination.
 */
export const AllCalls = () => {
    const [page, setPage] = useState(1); // API page (1-based)
    const [limit, setLimit] = useState(25);
    const [filters, setFilters] = useState({}); // dynamic filters (e.g., agent_id, date ranges)

    // Build params including optional filters dynamically (clean filters generically)
    const params = useMemo(() => {
        const base = { page, limit };

        // Clean filters: remove undefined/null/empty-string and empty arrays.
        // For filter keys ending with `-in` (convention for multi-selects) convert single values into single-element arrays.
        const cleaned = Object.entries(filters || {}).reduce((acc, [key, val]) => {
            if (val === undefined || val === null || val === '') return acc;
            if (Array.isArray(val)) {
                if (val.length === 0) return acc;
                acc[key] = val;
                return acc;
            }

            // Keys that end with `-in` are treated as multi-selects and should be sent as arrays.
            if (typeof key === 'string' && key.endsWith('-in')) {
                acc[key] = [val];
            } else {
                acc[key] = val;
            }
            return acc;
        }, {});

        return { ...base, ...cleaned };
    }, [page, limit, filters]);

    const { data, isLoading, isFetching, error, refetch } = useGetCalls(params, { keepPreviousData: true });

    const meta = data?.calls;
    const rows = meta?.data || [];
    const total = meta?.total || rows.length;

    // Handle filter changes and reset to page 1
    const handleFiltersChange = (newFilters) => {
        setFilters(newFilters);
        setPage(1);
    };

    const columns = useMemo(() => [
        { id: 'type', label: 'Type', accessor: 'type', width: 120 },
        { id: 'status', label: 'Status', accessor: 'status', width: 140 },
        { id: 'agent_id', label: 'Agent', accessor: 'agent_id', width: 140 },
        { id: 'ucid', label: 'UCID', accessor: 'ucid', width: 200 },
        { id: 'duration', label: 'Duration', accessor: 'duration', width: 100, render: (val) => val ?? '-' },
        { id: 'start_time', label: 'Start Time', accessor: 'start_time', width: 180, render: (val) => val ? new Date(val).toLocaleString() : '' },
        { id: 'end_time', label: 'End Time', accessor: 'end_time', width: 180, render: (val) => val ? new Date(val).toLocaleString() : '' },
        { id: 'createdAt', label: 'Created', accessor: 'createdAt', width: 180, render: (val) => val ? new Date(val).toLocaleString() : '' },
        { id: 'updatedAt', label: 'Updated', accessor: 'updatedAt', width: 180, render: (val) => val ? new Date(val).toLocaleString() : '' },
        {
            id: 'audio_file',
            label: 'Audio',
            accessor: 'audio_file',
            width: 200,
            dynamicRender: (row) => row.audio_file ? (
                <audio controls style={{ width: '180px' }}>
                    <source src={row.audio_file} />
                    Your browser does not support the audio element.
                </audio>
            ) : '-'
        },
    ], []);

    const actionsRenderer = (row) => [
        { label: 'View', onClick: (r) => console.log('View call', r) },
    ];

    const handleChangePage = (muiPage) => {
        // MUI TablePagination is zero-based; API is 1-based
        setPage(muiPage + 1);
    };

    const handleChangeRowsPerPage = (val) => {
        setLimit(val);
        setPage(1);
    };

    const filterConfig = useMemo(() => [
        {
            type: 'select',
            label: 'Agent',
            key: 'agent_id-in',
            options: [
                { value: 'agent-286', label: 'agent-286' },
                // Additional agents can be injected by parent in future
            ]
        },
        {
            type: 'dateRange',
            label: 'Start Time',
            key: 'start_time_range',
            fromKey: 'start_time-from',
            toKey: 'start_time-to'
        },
        {
            type: 'dateRange',
            label: 'End Time',
            key: 'end_time_range',
            fromKey: 'end_time-from',
            toKey: 'end_time-to'
        }
        ,
        {
            type: 'multiselect',
            label: 'Status',
            key: 'status-in',
            options: [
                { value: 'NotAnswered', label: 'NotAnswered' },
                { value: 'Answered', label: 'Answered' },
            ]
        },
        {
            type: 'multiselect',
            label: 'Type',
            key: 'type-in',
            // Values should match backend CALL_TYPE enum (see app/modules/crm/entities/calls.ts)
            options: [
                { value: 'InBound', label: 'InBound' },
                { value: 'Manual', label: 'Manual' },
                { value: 'Progressive', label: 'Progressive' },
            ]
        }
    ], []);

    return (
        <Box p={2}>
            <Typography variant="h5" gutterBottom>All Calls</Typography>

            <FilterableTable
                columns={columns}
                rows={rows}
                isLoading={isLoading || isFetching}
                error={error}
                actionsRenderer={actionsRenderer}
                getRowId={(row) => row._id}
                onSelectionChange={(sel) => console.log('Selected rows', sel)}

                serverPagination
                page={page - 1} // zero based for the table component
                rowsPerPage={limit}
                totalCount={total}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}

                filterConfig={filterConfig}
                initialFilters={{}}
                onFiltersChange={handleFiltersChange}
            />
        </Box>
    );
};

export default AllCalls;
